import { useCallback, useEffect, useState } from 'react';
import type { LookupsDto } from '@/api/types';

/**
 * OCR IDENTIFIER RANKING, per client / carrier / facility (#246 / #248).
 *
 * Pete, 2026-08-25: *"Confirmed the order but need a config screen by facility for
 * that. So we can change it if necessary. Maybe a overall setting and then you can
 * drill it down by client, carrier so we handle complexities or simplicities of all
 * our clients."* And then, on the shape of this screen: *"Please make the admin
 * screen look like the others if possible. Want to keep the same look and feel if
 * it makes sense."*
 *
 * ---- ⭐ IT IS DELIBERATELY DOOR DEDICATIONS ---------------------------------
 *
 * Same wrapper, same facility picker, same card, same stacked rows with an On
 * toggle and a × on the right, same "+ Add …" bottom-left and blue Save
 * bottom-right, and this file's own `apiFetch` and plain elements rather than the
 * shadcn components — exactly as `SearchWindowEditor` says: *"matching the file it
 * lives in is what keeps a panel from looking like it was bolted on."*
 *
 * ---- ⛔ THE THREE THINGS THIS SCREEN MUST NOT DO ----------------------------
 *
 * ⛔ IT MUST NOT SHOW AN EMPTY LIST AND MEAN "NOTHING IS HAPPENING". No rules is
 * the NORMAL state — the shipped default ranks every page. So the default is
 * printed, in order, where the rows would be.
 *
 * ⛔ IT MUST NOT HIDE THE RULES THAT APPLY EVERYWHERE. A rule with no facility
 * ranks paperwork in this building too. Hiding it would produce a screen that says
 * "no rules" while a rule quietly runs — the most expensive shape of bug this
 * codebase keeps re-learning.
 *
 * ⛔ AND IT MUST NOT OFFER A REGEX BOX. See `adHocIdentifiers.ts`: the inputs are
 * a literal label and an optional shape, because a pattern typed into a config
 * screen can hang the request, match everything, or match nothing with no error
 * anywhere.
 *
 * ⚠️ THE VOCABULARY COMES FROM THE SERVER. The six kinds, the searchable columns
 * and the caps are fetched, not hard-coded here — web/ cannot import them from
 * src/, and a second hand-typed copy would drift the day a seventh is added.
 */

async function apiFetch<T>(path: string, token: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`/api/${path}`, {
        ...options, credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token, ...(options?.headers ?? {}) },
    });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error ?? `HTTP ${res.status}`); }
    if (res.status === 204) return undefined as unknown as T;
    return res.json();
}

interface KindOption { kind: string; label: string; searchable: boolean }
interface ColumnOption { column: string; label: string }

interface AdHocRow {
    id: number | null;
    label: string;
    shape: string;      // '' = no shape
    column: string;
    isActive: boolean;
    _key: string;
}

interface RuleRow {
    id: number | null;
    facilityCode: string | null;   // null = every facility
    clientCode: string | null;
    carrierCode: string | null;
    rank: string[];                // ordered kinds
    priority: number;
    notes: string | null;
    isActive: boolean;
    adHoc: AdHocRow[];
    _key: string;
}

interface Vocab {
    defaultRank: string[];
    kinds: KindOption[];
    columns: ColumnOption[];
    maxAdHocFields: number;
    maxLabelLength: number;
    maxShapeLength: number;
}

let keySeq = 0;
const newKey = (p: string) => `${p}-${Date.now()}-${keySeq++}`;

interface Props {
    getToken: () => Promise<string>;
    lookups: LookupsDto;
    /** ⭐ The facility the calendar is showing — see DocumentConfigAdmin's note. */
    initialFacilityCode?: string | null;
}

/**
 * ⛔ THE CHIP LIST IS THE ORDER, AND THE ORDER IS THE WHOLE SETTING.
 *
 * ⚠️ NOT A ROW OF CHECKBOXES PLUS A SEPARATE "ORDER" FIELD. That would be two
 * settings for one fact (§1R.16) and they would disagree the first time somebody
 * unticked something. Included kinds sit in the top row, in order; excluded ones
 * sit below, greyed, one click from coming back.
 *
 * ⚠️ AND REMOVING ONE IS A REAL DECISION, WHICH THE SCREEN SAYS OUT LOUD. A kind
 * missing from a rank is DROPPED by the matcher — deliberately — so "not used"
 * means "we do not trust that number on this paperwork", not "we forgot".
 */
function RankChips(
    { rank, kinds, onChange }:
    { rank: string[]; kinds: KindOption[]; onChange: (next: string[]) => void },
) {
    const labelOf = (k: string) => kinds.find(x => x.kind === k)?.label ?? k;
    const searchable = (k: string) => kinds.find(x => x.kind === k)?.searchable !== false;
    const unused = kinds.filter(k => !rank.includes(k.kind));

    const move = (i: number, delta: number) => {
        const j = i + delta;
        if (j < 0 || j >= rank.length) return;
        const next = rank.slice();
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };

    return (
        <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
                {rank.length === 0 && (
                    <span className="text-[11px] text-red-600">
                        Nothing is being used — this rule would match nothing at all.
                    </span>
                )}
                {rank.map((k, i) => (
                    <span key={k}
                        className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 pl-2 pr-1 py-0.5 text-[11px] text-blue-900">
                        <span className="text-[9px] font-semibold text-blue-400">{i + 1}</span>
                        {labelOf(k)}
                        {/**
                          * ⚠️ #247 ON THE CHIP ITSELF. A PRO or a trailer can be READ off a
                          * page and matched against nothing, because dock.Appointment has no
                          * column for either. Ranking one without being told that is a
                          * setting that appears to work and never fires.
                          */}
                        {!searchable(k) && (
                            <span title="Read from the page, but there is no column to look it up in yet (#247)."
                                className="rounded bg-amber-100 px-1 text-[9px] font-semibold text-amber-800">read only</span>
                        )}
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                            title="Trust this one more" className="px-0.5 text-blue-400 hover:text-blue-700 disabled:opacity-25">↑</button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === rank.length - 1}
                            title="Trust this one less" className="px-0.5 text-blue-400 hover:text-blue-700 disabled:opacity-25">↓</button>
                        <button type="button" onClick={() => onChange(rank.filter(x => x !== k))}
                            title="Stop using this identifier on this paperwork"
                            className="px-0.5 text-blue-300 hover:text-red-600 leading-none">×</button>
                    </span>
                ))}
            </div>
            {unused.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">Not used</span>
                    {unused.map(k => (
                        <button key={k.kind} type="button" onClick={() => onChange([...rank, k.kind])}
                            title="Add to the bottom of the order"
                            className="rounded border border-dashed border-gray-300 px-2 py-0.5 text-[11px] text-gray-400 hover:border-blue-300 hover:text-blue-700">
                            + {k.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/** ⛔ The same alphabet `adHocIdentifiers.ts` compiles. Shown, never guessed at. */
function shapeProblem(shape: string): string | null {
    const s = shape.trim();
    if (s.length === 0) return null;                       // no shape is fine
    const bad = Array.from(s).find(ch => !'A9X /.-'.includes(ch));
    if (bad) return `'${bad}' is not allowed — use A, 9, X and - / . or a space`;
    if (!/[A9X]/.test(s)) return 'needs at least one A, 9 or X, or it can only match itself';
    return null;
}

function AdHocEditor(
    { row, vocab, onChange, onRemove }:
    { row: AdHocRow; vocab: Vocab; onChange: (patch: Partial<AdHocRow>) => void; onRemove: () => void },
) {
    const problem = shapeProblem(row.shape);
    return (
        <div className={`flex flex-wrap items-center gap-1.5 ${row.isActive ? '' : 'opacity-60'}`}>
            <input type="text" value={row.label} maxLength={vocab.maxLabelLength}
                onChange={e => onChange({ label: e.target.value })}
                placeholder="Words on the page, e.g. Job No"
                className="w-44 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <input type="text" value={row.shape} maxLength={vocab.maxShapeLength}
                onChange={e => onChange({ shape: e.target.value })}
                placeholder="Shape (optional)"
                title="A = a letter, 9 = a digit, X = either. Leave blank for anything."
                className={`w-36 rounded border px-2 py-1 font-mono text-sm focus:outline-none focus:ring-1 ${problem ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-400'}`} />
            <span className="text-xs text-gray-400">look up as</span>
            <select value={row.column} onChange={e => onChange({ column: e.target.value })}
                className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
                {vocab.columns.map(c => <option key={c.column} value={c.column}>{c.label}</option>)}
            </select>
            <button type="button" onClick={() => onChange({ isActive: !row.isActive })}
                title={row.isActive ? 'Active — click to turn off (keeps the field for later)' : 'Inactive — click to turn on'}
                className={`px-2 py-1 rounded text-[10px] font-semibold border shrink-0 ${row.isActive ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}>
                {row.isActive ? 'On' : 'Off'}
            </button>
            <button type="button" onClick={onRemove} title="Remove"
                className="text-gray-400 hover:text-red-600 text-base px-1 leading-none shrink-0">×</button>
            {problem && <span className="w-full text-[11px] text-red-600">Shape: {problem}</span>}
        </div>
    );
}

export default function OcrIdentifierRankAdmin({ getToken, lookups, initialFacilityCode }: Props) {
    const facilities = lookups.facilities;
    const [facilityCode, setFacilityCode] = useState(
        (initialFacilityCode && facilities.some(f => f.FacilityCode === initialFacilityCode)
            ? initialFacilityCode
            : facilities[0]?.FacilityCode) ?? '',
    );
    const [rows, setRows] = useState<RuleRow[]>([]);
    /** ⭐ The ids the screen was showing when it loaded — only these may be deleted. */
    const [loadedIds, setLoadedIds] = useState<number[]>([]);
    const [vocab, setVocab] = useState<Vocab | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    const adopt = useCallback((serverRows: Array<{
        id: number; facilityCode: string | null; clientCode: string | null; carrierCode: string | null;
        identifierRank: string; priority: number; notes: string | null; isActive: boolean;
        adHoc: Array<{ id: number; label: string; shape: string | null; column: string; sortOrder: number; isActive: boolean }>;
    }>) => {
        setRows(serverRows.map(r => ({
            id: r.id,
            facilityCode: r.facilityCode,
            clientCode: r.clientCode,
            carrierCode: r.carrierCode,
            rank: r.identifierRank.split(',').map(s => s.trim()).filter(Boolean),
            priority: r.priority,
            notes: r.notes,
            isActive: r.isActive,
            adHoc: r.adHoc
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(f => ({ id: f.id, label: f.label, shape: f.shape ?? '', column: f.column, isActive: f.isActive, _key: newKey('ah') })),
            _key: newKey('rule'),
        })));
        setLoadedIds(serverRows.map(r => r.id));
    }, []);

    const load = useCallback(async () => {
        if (!facilityCode) return;
        setLoading(true); setError(null); setSaved(false);
        try {
            const token = await getToken();
            const res = await apiFetch<{
                rows: Parameters<typeof adopt>[0];
                defaultRank: string[]; kinds: KindOption[]; columns: ColumnOption[];
                maxAdHocFields: number; maxLabelLength: number; maxShapeLength: number;
            }>(`mgmt/ocr-identifier-rank?facilityCode=${encodeURIComponent(facilityCode)}`, token);
            setVocab({
                defaultRank: res.defaultRank, kinds: res.kinds, columns: res.columns,
                maxAdHocFields: res.maxAdHocFields,
                maxLabelLength: res.maxLabelLength, maxShapeLength: res.maxShapeLength,
            });
            adopt(res.rows);
        } catch (e) {
            // ⚠️ NAMED, because the overwhelmingly likely cause on the first visit is
            // that migration 164 has not been run — and "Load failed" sends nobody
            // anywhere useful.
            setError(e instanceof Error
                ? `${e.message} (if this is the first visit, check that migration 164 has been run.)`
                : 'Could not load the ranking rules.');
        } finally { setLoading(false); }
    }, [facilityCode, getToken, adopt]);

    useEffect(() => { void load(); }, [load]);

    const updateRow = (key: string, patch: Partial<RuleRow>) => {
        setSaved(false);
        setRows(rs => rs.map(r => (r._key === key ? { ...r, ...patch } : r)));
    };
    const addRow = () => {
        setSaved(false);
        setRows(rs => [...rs, {
            id: null,
            // ⚠️ A NEW RULE STARTS SCOPED TO THIS FACILITY, not to everywhere. The
            // narrower default is the one whose blast radius a person can see.
            facilityCode,
            clientCode: null, carrierCode: null,
            rank: vocab ? [...vocab.defaultRank] : [],
            priority: 100, notes: null, isActive: true, adHoc: [],
            _key: newKey('rule'),
        }]);
    };
    const removeRow = (key: string) => { setSaved(false); setRows(rs => rs.filter(r => r._key !== key)); };

    const addField = (row: RuleRow) => {
        if (!vocab || row.adHoc.length >= vocab.maxAdHocFields) return;
        updateRow(row._key, {
            adHoc: [...row.adHoc, {
                id: null, label: '', shape: '',
                column: vocab.columns[0]?.column ?? 'ReferenceNumber',
                isActive: true, _key: newKey('ah'),
            }],
        });
    };

    const handleSave = async () => {
        if (!vocab) return;
        // ⛔ CHECKED HERE TOO, so a mistake is caught beside the box that made it —
        // the server checks again with the same code the matcher uses.
        for (const r of rows) {
            if (r.rank.length === 0) { setError('Every rule needs at least one identifier in its order.'); return; }
            for (const f of r.adHoc) {
                if (f.label.trim().length < 2) { setError('Every ad-hoc field needs a label of at least two characters.'); return; }
                const problem = shapeProblem(f.shape);
                if (problem) { setError(`Ad-hoc field '${f.label.trim()}': shape ${problem}.`); return; }
            }
            const seen = new Set<string>();
            for (const f of r.adHoc) {
                const k = f.label.trim().toUpperCase();
                if (seen.has(k)) { setError(`The ad-hoc field '${f.label.trim()}' is listed twice on one rule.`); return; }
                seen.add(k);
            }
        }

        setSaving(true); setError(null); setSaved(false);
        try {
            const token = await getToken();
            const res = await apiFetch<{ rows: Parameters<typeof adopt>[0] }>('mgmt/ocr-identifier-rank', token, {
                method: 'PUT',
                body: JSON.stringify({
                    facilityCode,
                    loadedIds,
                    rows: rows.map(r => ({
                        id: r.id,
                        facilityCode: r.facilityCode,
                        clientCode: r.clientCode,
                        carrierCode: r.carrierCode,
                        identifierRank: r.rank.join(','),
                        priority: r.priority,
                        notes: r.notes,
                        isActive: r.isActive,
                        adHoc: r.adHoc.map((f, i) => ({
                            id: f.id,
                            label: f.label.trim(),
                            shape: f.shape.trim() ? f.shape.trim() : null,
                            column: f.column,
                            sortOrder: i,
                            isActive: f.isActive,
                        })),
                    })),
                }),
            });
            // ⭐ REDRAWN FROM WHAT WAS STORED, not from what we hoped we sent —
            // which is how new rows learn their ids without a second load.
            adopt(res.rows);
            setSaved(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Save failed.');
        } finally { setSaving(false); }
    };

    const selectedFac = facilities.find(f => f.FacilityCode === facilityCode);

    return (
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
            <div>
                <h1 className="text-xl font-semibold text-gray-900">OCR Identifier Ranking</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                    When a scanned page carries several numbers, this decides which one is used to find the appointment.
                </p>
            </div>

            <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Facility</label>
                <select value={facilityCode} onChange={e => setFacilityCode(e.target.value)}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                    {facilities.map(f => (
                        <option key={f.FacilityCode} value={f.FacilityCode}>{f.DisplayName} ({f.FacilityCode})</option>
                    ))}
                </select>
                {selectedFac && <span className="text-xs text-gray-400">{selectedFac.Timezone}</span>}
            </div>

            {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}

            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
                <div>
                    <h3 className="text-sm font-semibold text-gray-800">Which number wins</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        A bill of lading usually shows a PO, a container and a trailer, and they can point at different
                        appointments. Rules are matched most-specific-first and a <strong>client rule beats a carrier rule</strong>;
                        a rule left as &ldquo;all facilities&rdquo; applies everywhere, which is why those rules are listed
                        here too. Anything you remove from an order is <em>not used at all</em> on that paperwork — that is
                        the point of removing it. With no rule at all, the order below applies.
                    </p>
                </div>

                {loading ? (
                    <p className="text-xs text-gray-400">Loading…</p>
                ) : !vocab ? null : (
                    <>
                        {rows.length === 0 ? (
                            /**
                             * ⛔ NO RULES IS THE NORMAL STATE AND MUST NOT READ AS "NOTHING
                             * IS HAPPENING". The shipped order is printed where the rows
                             * would be, so an empty screen still says what OCR will do.
                             */
                            <div className="rounded-lg border border-dashed border-gray-200 p-3">
                                <p className="text-xs text-gray-500">
                                    No rules — every client&rsquo;s paperwork is read in the shipped order:
                                </p>
                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                    {vocab.defaultRank.map((k, i) => (
                                        <span key={k} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
                                            <span className="text-[9px] font-semibold text-gray-400">{i + 1}</span>
                                            {vocab.kinds.find(x => x.kind === k)?.label ?? k}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {rows.map(r => (
                                    <div key={r._key}
                                        className={`rounded-lg border border-gray-100 bg-gray-50/50 p-2 space-y-2 ${r.isActive ? '' : 'opacity-60'}`}>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <select value={r.facilityCode === null ? '*' : r.facilityCode}
                                                onChange={e => updateRow(r._key, { facilityCode: e.target.value === '*' ? null : facilityCode })}
                                                title="A rule can apply to this site only, or to every site."
                                                className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
                                                <option value={facilityCode}>This facility</option>
                                                <option value="*">All facilities</option>
                                            </select>
                                            <select value={r.clientCode ?? ''}
                                                onChange={e => updateRow(r._key, { clientCode: e.target.value || null })}
                                                className="min-w-[9rem] rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
                                                <option value="">Any client</option>
                                                {lookups.clients.map(c => <option key={c.ClientCode} value={c.ClientCode}>{c.ClientName}</option>)}
                                            </select>
                                            <select value={r.carrierCode ?? ''}
                                                onChange={e => updateRow(r._key, { carrierCode: e.target.value || null })}
                                                className="min-w-[9rem] rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
                                                <option value="">Any carrier</option>
                                                {lookups.carriers.map(c => <option key={c.CarrierCode} value={c.CarrierCode}>{c.CarrierName}</option>)}
                                            </select>
                                            <label className="flex items-center gap-1 text-xs text-gray-500">
                                                Priority
                                                <input type="number" min={0} max={9999} value={r.priority}
                                                    title="Lower runs first among rules that are equally specific."
                                                    onChange={e => updateRow(r._key, { priority: parseInt(e.target.value, 10) || 0 })}
                                                    className="w-16 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                            </label>
                                            <div className="flex-1" />
                                            <button type="button" onClick={() => updateRow(r._key, { isActive: !r.isActive })}
                                                title={r.isActive ? 'Active — click to turn off (keeps the rule for later)' : 'Inactive — click to turn on'}
                                                className={`px-2 py-1 rounded text-[10px] font-semibold border shrink-0 ${r.isActive ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}>
                                                {r.isActive ? 'On' : 'Off'}
                                            </button>
                                            <button type="button" onClick={() => removeRow(r._key)} title="Remove"
                                                className="text-gray-400 hover:text-red-600 text-base px-1 leading-none shrink-0">×</button>
                                        </div>

                                        <div className="flex items-start gap-2">
                                            <span className="text-[10px] uppercase tracking-wide text-gray-400 w-14 shrink-0 pt-1">Order</span>
                                            <div className="min-w-0 flex-1">
                                                <RankChips rank={r.rank} kinds={vocab.kinds}
                                                    onChange={next => updateRow(r._key, { rank: next })} />
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-2">
                                            <span className="text-[10px] uppercase tracking-wide text-gray-400 w-14 shrink-0 pt-1">Extras</span>
                                            <div className="min-w-0 flex-1 space-y-1.5">
                                                {r.adHoc.length === 0 ? (
                                                    <p className="text-[11px] text-gray-400">
                                                        None. Add one to read something this client prints that is not in the list above.
                                                    </p>
                                                ) : (
                                                    r.adHoc.map(f => (
                                                        <AdHocEditor key={f._key} row={f} vocab={vocab}
                                                            onChange={patch => updateRow(r._key, {
                                                                adHoc: r.adHoc.map(x => (x._key === f._key ? { ...x, ...patch } : x)),
                                                            })}
                                                            onRemove={() => updateRow(r._key, { adHoc: r.adHoc.filter(x => x._key !== f._key) })} />
                                                    ))
                                                )}
                                                <div className="flex items-center gap-3">
                                                    <button type="button" onClick={() => addField(r)}
                                                        disabled={r.adHoc.length >= vocab.maxAdHocFields}
                                                        className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-40 disabled:no-underline">
                                                        + Add ad-hoc field
                                                    </button>
                                                    {r.adHoc.length > 0 && (
                                                        <span className="text-[10px] text-gray-400">
                                                            {/**
                                                              * ⛔ THE ORDERING SAID OUT LOUD. Ad-hoc fields run
                                                              * AFTER the list above and can only ever add reach —
                                                              * an ordering nobody states is one nobody can rely on.
                                                              */}
                                                            Tried after the order above, top to bottom.
                                                            {' '}A = a letter, 9 = a digit, X = either.
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-1">
                            <button type="button" onClick={addRow}
                                className="text-xs text-blue-600 hover:text-blue-800 underline">+ Add rule</button>
                            <div className="flex items-center gap-3">
                                {saved && <span className="text-xs text-green-600">Saved</span>}
                                <button type="button" onClick={handleSave} disabled={saving}
                                    className="px-4 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors">
                                    {saving ? 'Saving…' : 'Save ranking'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
